/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, ViewChild } from "@angular/core";

import { ActivatedRoute } from "@angular/router";

import { fromEvent, Subject, Subscription, } from "rxjs";
import { map, debounceTime} from "rxjs/operators";

import { EventInstance } from "app/models/event-instance";
import { ImageService } from "app/services/image.service";
import { InstanceFunctionDialogComponent } from "app/components/locate/instance-function-dialog/instance-function-dialog.component";
import { InstanceDataDialogComponent } from "app/components/locate/instance-data-dialog/instance-data-dialog.component";
import { NewMapComponent, TagClickedEvent } from "app/components/map/new-map.component";
import { EventUuid_TagEnteredLocation, EventUuid_TagEnteredZone, EventUuid_TagExitedLocation, EventUuid_TagExitedZone, EventUuid_TagMovement } from "app/event-constants";
import { PagedResults } from "app/models/paged-results";
import { LocationInstance } from "app/models/location-instance";
import { LocationService } from "app/services/location.service";
import { PropertyType } from "app/models/property-type";
import { PagingControls } from "app/models/paging-controls";
import { CookieService } from "ngx-cookie-service";
import { SimpleConfirmDialogComponent } from "app/components/simple-confirm-dialog/simple-confirm-dialog.component";
import { SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { TagInstance } from "app/models/tag-instance";
import { TagService } from "app/services/tag.service";
import { SaveTagListDialogComponent } from "./save-tag-list-dialog/save-tag-list-dialog.component";
import { Property } from "app/models/property";
import { MonitorTags_SelectedTagWebSocketService } from "app/services/monitor-tags-selected-tag-web-socket-service";

/** Declare $ to support bootstrap calls in code */
declare var $: any;

/**
 * Number of Tags to Load / Display on initial display and when searching.
 * This is the maximum number of Tags that will be shown to the User for selecting.
 * */
export const MonitorTags_TagsPerPage: number = 50;

/** Maximum number of Tags that can be selected / tracked at once. */
export const MonitorTags_MaxSelectedTags: number = 100;

/** Maximum number of characters of a Filter Tag's label shown in the "selected, $filterName" label
 *  (see "isTagToSelectSelectedUnderOtherFilter") before it gets truncated with "...". */
export const MonitorTags_MaxFilterNameLabelLength: number = 25;

@Component({
  templateUrl: './monitor-tags.component.html',
  styleUrls: ['./monitor-tags.component.css']
})
export class MonitorTagsComponent implements OnInit, OnDestroy {

  /** MapComponent for viewing the location chosen inside the Locate Component */
  @ViewChild('locateMap') mapComponent: NewMapComponent;

  /** Reference to the AssetFunctionDialog used to execute Function Action(s) on Tags */
  @ViewChild(InstanceFunctionDialogComponent) instanceFunctionDialogComponent: InstanceFunctionDialogComponent;

  /** Reference to the InstanceDataDialog used to execute the "Edit Data" Action on Tags */
  @ViewChild(InstanceDataDialogComponent) instanceDataDialogComponent: InstanceDataDialogComponent;

  /** Reference to the SimpleConfirmDialog used to confirm User Actions before going through with them */
  @ViewChild(SimpleConfirmDialogComponent) simpleConfirmDialogComponent: SimpleConfirmDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to notify the User of things like the Selection Limit being reached */
  @ViewChild('successErrorDialog') simpleSuccessErrorDialogComponent!: SimpleSuccessErrorDialogComponent;

  /** Tags to display in the "Select Tags" Tab.  These are the initial Tags to select loaded on load, and any that match the "search" value */
  tagsToSelect: TagInstance[] = [];

  /** Tags selected to be shown in the List and traced at the Location / Zone level. */
  tagsSelected: TagInstance[] = [];

  /** Assets selected to be shown in the List and have passed any "Search" criteria that is going on.  This are the ones that are actually displayed in the List. */
  selectedTagsToDisplay: TagInstance[] = [];

  /** Holds the Tags being toggled from the "Select Tags" Tab to the "Selected Tags" tab.  */
  selectTagsBeingToggled: Map<number, TagInstance> = new Map();

  /** Holds the Tags being selected in the "Selected Tags" tab */
  tagsSelectedBeingToggled: Map<number, TagInstance> = new Map();

  /** Holds the Tags in the "Selected Tags" List tied to their Web Socket connection  */
  tagsBeingTracked: Map<number, WsConnection> = new Map();

  /** Holds the Location thta is currently being displayed */
  locationBeingDisplayed: LocationInstance = null;

  /** Holds the Locations that are available for display.  The "locationBeingDisplayed" will be one of these. */
  locationsToDisplay: LocationInstance[] = [];

  /** Boolean Flag used to hold whether we are selecting / unselecting all the Tags in the "Select Tags" List */
  isSelectingAllTagsToSelect = false;

  /** Boolean Flag used to hold wheter we are selecting / unselecting all the Tags in the "Selected Assets" List */
  isSelectingAllSelectedTags = false;

  /** Filter Tags - one per distinct Search Option + Search Value combination that at least one
   *  currently selected Tag was chosen under.  See "SelectionFilterTag". */
  filterTags: SelectionFilterTag[] = [];
  /** The Filter Tag currently being "viewed" - its Tags are shown inline in the "Select Tags"
   *  List instead of the normal Search results.  Null when browsing normally. */
  activeFilterTag: SelectionFilterTag | null = null;
  /** Toggle for pinning untagged selected Tags (selected with no Filter active) to the top of
   *  Page 1 of the "Select Tags" List.  See "getTagsToSelectDisplayList". */
  pinUntaggedSelectionsOnTop: boolean = true;

  /**
   * Components related to the "Select Tags" Search Functionality
   */
  // HTML Input Element used for the Asset search value
  @ViewChild('selectTagsSearchInput') selectTagsSearchInput!: ElementRef;
  // Boolean Flag used to indicate if we are currently querying the backend for "Search Tag(s) to select" requests. Used to show a "Loading" indicator to the User.
  isSearchingTagsToSelect = false;
  // Value entered for searching Tag
  tagsToSelectSearchValue = "";
  // Options available for searching Tag
  tagsToSelectSearchOptions: SearchOption[] = [];
  // Selected Search Option for Tag
  selectedTagsToSelectSearchOption: SearchOption;
  // Boolean flag used to indicate if the Search Option for the Select Tags tab is changing. (Used to disable some UI while things are loaded)
  isTagsToSelectSearchOptionChanging = false;

  /**
   * Components related to the "Selected Tags" Search Functionality
   */
  // HTML Input Element used for the Asset search value
  @ViewChild('selectedTagsSearchInput') selectedTagsSearchInput!: ElementRef;
  // Boolean Flag used to indicate if we are currently filtering the selected Tags to display in the "Selected Tag(s)" tab. Used to show a "Loading" indicator to the User.
  isSearchingSelectedTags = false;
  // Value entered for searching Tag
  selectedTagsSearchValue = "";
  // Selected Search Option for Tag
  selectedTagsSearchOption: SearchOption;
  // Boolean flag used to indicate if the Search Option for the Selected Tags tab is changing. (Used to disable some UI while things are loaded)
  isSelectedTagsSearchOptionChanging = false;

  /*
   * Components related to the "Saved Tag Lists" functionality
   */
  // Dialog used to have the User enter the name when saving an Asset List
  @ViewChild(SaveTagListDialogComponent) saveTagListDialogComponent: SaveTagListDialogComponent;
  // Stored Tag List array of the Tag Lists that are available / saved
  storedTagList: StoredTagListItem[] = [];
  // Flag which holds whether an Tag List is being deleted
  isDeletingStoredTagList = false;
  // Flag which holds whether an Tag List is being saved
  isSavingCurrentTagList = false; 
  // Flag which holds whether an Tag List is being loaded
  isLoadingTagList = false;
  // Reference to the last Tag List loaded or saved.  Used to populate the default "name" in the SaveTagListDialog
  lastLoadedOrSavedTagList: StoredTagListItem = null;
  // Stored Tag List Item selected to be deleted.  Remembered here while the "Confirm" Dialog is displayed / handled.
  tagListBeingDeleted: StoredTagListItem = null;

    /** Total # of Tags. (Used to figure out # of pages) */
    totalNumberOfTags = 0;
    /** Current Page being displayed */
    currentPage= 1;
    /** Exposes MonitorTags_TagsPerPage to the Template (module-level constants aren't visible there) */
    tagsPerPage = MonitorTags_TagsPerPage;

  /** 
   * Constructor
   */
  constructor(
    private tagService: TagService,
    private locationService: LocationService,
    private imageService: ImageService,
    private route: ActivatedRoute,
    private selectedTagWebSocketService: MonitorTags_SelectedTagWebSocketService,
    private cookieService: CookieService
  ) { }

  /** 
   * OnInit() - Called after the UI is initialized, but before it is displayed. 
   */
  ngOnInit(): void {
    // Grab the Data that was resolved in the background as the page was loading...
    this.route.data.subscribe(
      (data: { 
        initialTags: PagedResults<TagInstance>,
        tagSearchProperties: Property[]
      }) => { 
        // Load the initial Tags to display 
        const initialTags: PagedResults<TagInstance> = data.initialTags;
        if (initialTags?.data != null) {
          this.tagsToSelect = initialTags.data;
          this.totalNumberOfTags = initialTags.size;
        }
        

        // Load the Search Options for Tags
        {
          const searchOptions: SearchOption[] = [];
          // Add the default "Name" Option
          searchOptions.push(new SearchOption());
          // Add the options for each search property found
          data.tagSearchProperties?.forEach(tagSearchProperty => {
            searchOptions.push(new SearchOption(tagSearchProperty));
          });
          this.tagsToSelectSearchOptions = searchOptions;

          // Set the default Search Option to the NAME option (always the first one)
          this.selectedTagsToSelectSearchOption = searchOptions[0];
          this.selectedTagsSearchOption = searchOptions[0];
        }
      });

    /*
     * Setup the Stored Tag List (Saved in a cookie)
     */
    const value = this.cookieService.get('page[monitor-tags]-key[saved-tags-list]');
    console.log("Stored Value: " + value);
    if (value?.length > 0) {
      this.storedTagList = JSON.parse(value);
    } else {
      this.saveStoredTagList();
    }
  }

  /** 
   * AfterViewInit() - Called after OnInit and after the UI is displayed. 
   * Use this to interact with the UI elements
   */
  ngAfterViewInit(): void {
    /*
     * Set up the "Select Tag(s) Search" functionality
     */
    fromEvent(this.selectTagsSearchInput.nativeElement, 'keyup')
      .pipe(
        // On any Key Up Event, set that we are searching Tags to Select and return the value
        map((event: any) => {
          this.isSearchingTagsToSelect = true;
          return event.target.value;
        }),

        // Debounce to limit REST API requests.  Give the User half a second to stop typing before starting to search. 
        debounceTime(500)
      )
      .subscribe((text: string) => {
        // Perform the Search
        this.searchTagsToSelect();
      });

      /*
       * Set up the "Selected Tag(s) Search" functionality
       */
    fromEvent(this.selectedTagsSearchInput.nativeElement, 'keyup')
      .pipe(
        // On any Key Up Event, set that we are searching Tags to Select and return the value
        map((event: any) => {
          this.isSearchingSelectedTags = true;
          return event.target.value;
        }),

        // Debounce to limit REST API requests.  Give the User half a second to stop typing before starting to search. 
        debounceTime(500)
      )
      .subscribe((text: string) => {
        this.refreshDisplayedSelectedTagsInList();
        this.isSearchingSelectedTags = false;
      });
  }

  /** 
   * Called when the Component is being destroyed and used to cleanup resources.
   * Currently closes all WebSockets used for the selected Assets.
   */
  ngOnDestroy(): void {
    // Need to close all open web sockets...
    this.selectAllTagsToSelect(false);
  }

  /**
   * Called by the UI when the "Select Tags" List's Search Option 
   * is changed.  This re-searches the data if needed. 
   */
  onTagsToSelectSearchOptionChanged(): void {
    // If the "Select Tags" List is being searched, nothing needs to be done.
    if (this.isSearchingTagsToSelect) {
      return;
    }
    // If the Search Value isn't specified, then we aren't displaying search results and nothing needs to be done.
    else if (this.tagsToSelectSearchValue?.length == 0) {
      return;
    }

    // Re-search the list
    this.isTagsToSelectSearchOptionChanging = true;
    this.isSearchingTagsToSelect = true;
    this.searchTagsToSelect();
  }

  /**
   * Performs the Search Functionality for the "Select Tags" List.
   * 
   * This queries the backend for a new page of Tags depending on the
   * selected Search Option and the Search Value.  
   */
  async searchTagsToSelect(): Promise<void> {
    // A new Search leaves whatever Filter Tag was being "viewed" and goes back to normal Search
    // results, and starts back on Page 1
    this.activeFilterTag = null;
    this.currentPage = 1;

    // Load Page 1 of the Tags to Select for the current Search Option / Value
    await this.loadTagsToSelectPage(this.currentPage);

    // Update the Flags used to control some UI properties
    this.isSearchingTagsToSelect = false;
    this.isTagsToSelectSearchOptionChanging = false;
  }

  /**
   * Fetches the given Page of Tags to Select from the backend, based on the
   * currently selected Search Option / Value, and updates "tagsToSelect" /
   * "totalNumberOfTags" with the results.
   *
   * Used both when performing a new Search (always Page 1) and when the User
   * pages through the "Select Tags" List (onPageChange).
   */
  private async loadTagsToSelectPage(pageNumber: number): Promise<void> {
    // Get the current search values for the request
    const searchOption = this.selectedTagsToSelectSearchOption;
    const searchValue = this.tagsToSelectSearchValue;
    console.log(`Searching Tags to Select with Search Option: '${searchOption?.searchType}/${searchOption?.property?.name}', Search Value: '${searchValue}', Page: ${pageNumber}`);

    // Get the Search Type, if there is no search value set the Search Type to NAME as it should display everything.
    let searchType = searchOption?.searchType;
    if (searchValue == null || searchValue.length == 0) {
      searchType = SearchType.NAME;
    }

    // Compute the starting index (0 based) for the Page being requested
    const start = (pageNumber - 1) * MonitorTags_TagsPerPage;

    // Variable used to hold the Tags being retrieved from the backend...
    const tags: TagInstance[] = [];

    // Perform the Search based on the Search Type
    switch (searchType) {
      case SearchType.PROPERTY: {
        // Property Search - Search against a Tag's Data Value for a specific Property
        try {
          const pagingControls = new PagingControls();
          pagingControls.start = start;
          pagingControls.size = MonitorTags_TagsPerPage;
          pagingControls.order = "ASC";

          const pagedData = await this.tagService.propertySearchTags(searchOption?.property?.uuid, searchValue, pagingControls);
          if (pagedData?.data != null) {
            tags.push(...pagedData.data);
            this.totalNumberOfTags = pagedData.size;
          }
        } catch (error) {
          console.error("Problem search Tags by Property.", error);
        }
        break;
      }
      case SearchType.NAME:
      default: {
        // Name Search - Search against the Tag's Name
        try {
          const pagedData = await this.tagService.getAllTagInstancesForPaging(start, MonitorTags_TagsPerPage, "ASC", searchValue);
          if (pagedData?.data != null) {
            tags.push(...pagedData.data);
            this.totalNumberOfTags = pagedData.size;
          }
        } catch (error) {
          console.error("Problem searching Tags by Name.", error);
        }
        break;
      }
    }

    // Update the Tags being displayed for selection
    this.tagsToSelect = tags;
  }

  /**
   * Returns the Tags to be displayed in the "Select Tags" List for the current Search / Page.
   */
  getTagsToSelectDisplayList(): TagInstance[] {
    if (!this.pinUntaggedSelectionsOnTop || this.currentPage !== 1 || !this.isNoFilterActive()) {
      return this.tagsToSelect;
    }

    const isPinnableUntaggedSelection = (tag: TagInstance) =>
      this.isTagToSelectSelected(tag) && this.isTagSelectionUntagged(tag?.id);

    const idsInCurrentPage = new Set(this.tagsToSelect.map(tag => tag?.id));
    const pinnedNotInPage = this.tagsSelected.filter(tag =>
      !idsInCurrentPage.has(tag?.id) && this.isTagSelectionUntagged(tag?.id)
    );
    const pinnedInPage = this.tagsToSelect.filter(tag => isPinnableUntaggedSelection(tag));
    const restOfPage = this.tagsToSelect.filter(tag => !isPinnableUntaggedSelection(tag));
    return [...pinnedNotInPage, ...pinnedInPage, ...restOfPage];
  }

  /**
   * Returns a boolean indicating if the given Tag ID is currently selected but not covered by any
   * Filter Tag (i.e. it was selected while no Filter / Search Value was active).
   */
  private isTagSelectionUntagged(tagId: number): boolean {
    return this.filterTags.every(filterTag => !filterTag.tagIds.includes(tagId));
  }

  /**
   * Returns a boolean indicating if the given Tag is being shown at the top of Page 1 of the
   * "Select Tags" List as an untagged selection (see "getTagsToSelectDisplayList").  Used by the
   * UI to label these Tags so the User understands why they're pinned to the top.
   */
  isTagToSelectPinnedUntagged(tag: TagInstance): boolean {
    return this.pinUntaggedSelectionsOnTop
      && this.currentPage === 1
      && this.isNoFilterActive()
      && this.isTagToSelectSelected(tag)
      && this.isTagSelectionUntagged(tag?.id);
  }

  /**
   * Returns a boolean indicating if no Filter (Search Value) is currently applied to the "Select
   * Tags" List - i.e. it's just showing the full, unfiltered browse list.
   */
  private isNoFilterActive(): boolean {
    return this.tagsToSelectSearchValue == null || this.tagsToSelectSearchValue.length === 0;
  }

  /**
   * Returns the Filter Tag the given Tag ID was selected under, if any (see
   * "recordTagSelectionUnderFilter"). Returns "undefined" if the Tag isn't currently selected
   * under any Filter Tag.
   */
  private getTagToSelectFilterTag(tagId: number): SelectionFilterTag | undefined {
    return this.filterTags.find(filterTag => filterTag.tagIds.includes(tagId));
  }

  /**
   * Returns a boolean indicating if the given Tag is already selected under a Filter Tag
   * different from the Filter currently being displayed in the "Select Tags" List (an active
   * Filter Tag being viewed, or the current Search Option / Search Value) - i.e. it also matches
   * the Filter being displayed, but was selected while a different Filter was active. Used by the
   * UI to label these Tags so the User understands they're already selected, just under another
   * Filter.
   */
  isTagToSelectSelectedUnderOtherFilter(tag: TagInstance): boolean {
    if (!this.isTagToSelectSelected(tag) || this.isNoFilterActive()) {
      return false;
    }

    const filterTag = this.getTagToSelectFilterTag(tag?.id);
    if (filterTag == null) {
      return false;
    }

    const isCurrentlyDisplayedFilter = this.activeFilterTag != null
      ? filterTag === this.activeFilterTag
      : this.isSameFilterTag(filterTag, this.selectedTagsToSelectSearchOption, this.tagsToSelectSearchValue);

    return !isCurrentlyDisplayedFilter;
  }

  /**
   * Returns the (possibly truncated) label of the other Filter Tag the given Tag was selected
   * under, for display alongside "isTagToSelectSelectedUnderOtherFilter".
   */
  getTagToSelectOtherFilterLabel(tag: TagInstance): string {
    const filterTag = this.getTagToSelectFilterTag(tag?.id);
    if (filterTag == null) {
      return "";
    }

    const label = this.getFilterTagLabel(filterTag);
    return label.length > MonitorTags_MaxFilterNameLabelLength
      ? `${label.substring(0, MonitorTags_MaxFilterNameLabelLength)}...`
      : label;
  }

  /**
   * Returns a boolean indicating if all the Tags in the "Search Tags" List
   * have been selected to be in the "Selected Tags" List where they are tracked. 
   */
  isAllTagsToSelectSelected(): boolean {
    const tagsToSelect = this.getTagsToSelectDisplayList();
    if (tagsToSelect.length == 0) {
      return false;
    }
    return tagsToSelect.find(tag => !this.tagsBeingTracked.has(tag?.id)) == null;
  }

  /**
   * Tied to the UI Checkbox at the top of the "Search Tags" List. 
   * 
   * This is used to either select all or unselect all of the currently
   * listed "Search Tag"s.  
   */
  selectAllTagsToSelect(select: boolean): void {
    this.isSelectingAllTagsToSelect = true;
    this.selectAllTagsToSelectAsync(select);
  }

  /**
   * Handles the "Select All" and "Unselect All" process for the "Search Tags" List.
   * This either adds all or removes all of the Tags currently in the Search List to 
   * the "Selected Tags" List depending on the given "select" parameter.
   */
  private async selectAllTagsToSelectAsync(select: boolean): Promise<void> {
    console.log(`Selecting All Search Tags: ${select}`);

    const searchOption = this.selectedTagsToSelectSearchOption;
    const searchValue = this.tagsToSelectSearchValue;
    const tagsToSelect = this.getTagsToSelectDisplayList();

    const promises: Promise<void>[] = [];
    if (select) {
      const notYetTrackedTags = tagsToSelect.filter(tag => !this.tagsBeingTracked.has(tag.id));
      const availableRoom = Math.max(MonitorTags_MaxSelectedTags - this.tagsSelected.length, 0);
      const tagsToActuallySelect = notYetTrackedTags.slice(0, availableRoom);

      tagsToActuallySelect.forEach(tag => {
        this.selectTagsBeingToggled.set(tag.id, tag);
        promises.push(this.addSelectTagToTagList(tag, true, searchOption, searchValue)) ;
      });

      if (tagsToActuallySelect.length < notYetTrackedTags.length) {
        this.showSelectionLimitReachedDialog();
      }
    } else {
      tagsToSelect.forEach(tag => {
        if (this.tagsBeingTracked.has(tag.id)) {
          this.selectTagsBeingToggled.set(tag.id, tag);
          promises.push(this.removeSelectTagFromTagList(tag)) ;
        }
      });
    }
    await Promise.all(promises);

    this.isSelectingAllTagsToSelect = false;
  }

  /**
   * Called by the UI when an "Searched" Tag is checked / unchecked.
   * This either adds or removes the Tag from the selected Tags
   * in the other Tab.  
   * 
   * This also either starts or stops tracking the Tag by opening 
   * or closing a Web Socket.  It starts with onlylistening for 
   * "Entered / Exited" / "Location / Zone" Events but can be sent a 
   * message to toggle the "Movement" Events as well. 
   * 
   * This essentially opens a Web Socket connection for every Asset in
   * that list, which may be a problem when there are a bunch. 
   */
  onTagToSelectToggled(tag: TagInstance, event): void {
    console.log(`On Select Tag Toggled!  Tag: '${tag?.id}/${tag?.name}', Checked: ${event?.target?.checked}`);
    if (tag?.id == null) {
      console.warn("Ignoring Select Tag being toggled since ID was not found.");
      return;
    }
    if (event?.target?.checked && this.tagsSelected.length >= MonitorTags_MaxSelectedTags) {
      event.target.checked = false;
      this.showSelectionLimitReachedDialog();
      return;
    }

    // Add the Tag being selected to the Map of Tags being toggled....
    this.selectTagsBeingToggled.set(tag.id, tag);

    // Depending on if this is a checked / unchecked event, add / remove the Select Tag from the Tags List.
    if (event?.target?.checked) {
      this.addSelectTagToTagList(tag, true, this.selectedTagsToSelectSearchOption, this.tagsToSelectSearchValue);
    } else {
      this.removeSelectTagFromTagList(tag);
    }
  }

  /**
   * Adds the given Tag to the "Selected Tags" List.
   */
  private async addSelectTagToTagList(tag: TagInstance, refresh: boolean, filterSearchOption?: SearchOption, filterSearchValue?: string): Promise<void> {
    // Enforce the maximum number of Tags that can be selected at once.
    if (this.tagsSelected.length >= MonitorTags_MaxSelectedTags) {
      this.selectTagsBeingToggled.delete(tag.id);
      this.showSelectionLimitReachedDialog();
      return;
    }

    // Get a fresh version of the Tag being selected and refresh the Lists if needed....
    if (refresh) {
      let freshTag: TagInstance = null;
      try {
        freshTag = await this.tagService.getTagInstanceById(tag.id);
      } catch (error) {
        console.warn("Problem obtaining fresh data on Tag being selected in the Select Tags Tab.  Possibly stale data will be used.");
      }
      if (freshTag != null) {
        tag = freshTag;
        this.updateDisplayedTag(tag);
      }
    }

    // Add the Selected Tag to the tagsSelected[] and sort it
    this.tagsSelected.push(tag);
    this.tagsSelected.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      } else if (a.name == b.name) {
        return 0;
      } else {
        return 1;
      }
    });

    // Record which Filter (if any) this Tag was selected under, for the Filter Tags UI.
    this.recordTagSelectionUnderFilter(tag.id, filterSearchOption, filterSearchValue);

    // Start tracking the Tag
    if (this.tagsBeingTracked.has(tag.id)) {
      console.warn("NOT creating WS connection for Asset as it is already being tracked...");
    } else {
      const wsConnection = new WsConnection();
      wsConnection.wsSubject = this.selectedTagWebSocketService.create(tag.id);
      wsConnection.wsSubscription = wsConnection.wsSubject.subscribe(
        message => { 
           this.handleTagWebSocketMessage(message, tag); 
        },
        error => {
          console.error(`Error detected opening WS Connection for Asset: '${tag.id}/${tag.name}'!`, error);
          wsConnection.close();
        }
      );
      this.tagsBeingTracked.set(tag.id, wsConnection);

      this.refreshDisplayedSelectedTagsInList();

      // Auto-select the Tag to be displayed in the Map on the RHS, same as if the User had
      // flipped to the "Selected Tags" Tab and checked it there themselves - the max number of
      // Tags that can be selected (MonitorTags_MaxSelectedTags) was already enforced above.
      await this.startTrackingSelectedTag(tag);
    }

    // Remove the Tag from the Map of Select Tags which are being toggled.
    this.selectTagsBeingToggled.delete(tag.id);
  }

  /**
   * Removes the given Tag from the "Selected Tags" List
   */
  private async removeSelectTagFromTagList(tag: TagInstance): Promise<void> {
    // Remove the Tag from the Selected Tags List
    this.tagsSelected = this.tagsSelected.filter(a => a?.id != tag?.id);

    // No longer selected, so drop it from any Filter Tag(s) it was recorded under.
    this.removeTagFromFilterTags(tag?.id);

    // Stop tracking the Tag
    if (!this.tagsBeingTracked.has(tag.id)) {
      console.warn("NOT destroying WS connection for Tag as it doesn't exist.");
    } else {
      if (this.tagsBeingTracked.get(tag.id).movementEventsEnabled) {
        await this.stopTrackingSelectedTag(tag);
      }

      const wsConnection = this.tagsBeingTracked.get(tag.id);
      wsConnection.close();
      this.tagsBeingTracked.delete(tag.id);
    }

    // Remove the Tag from the Map of Select Tags which are being toggled.
    this.selectTagsBeingToggled.delete(tag.id);

    this.refreshDisplayedSelectedTagsInList();
  }

  /**
   * Returns a boolean indicating if the givne Tag is being 
   * toggled between states for the "Search Tags" and "Selected Tags".
   * 
   * Used by the UI to disable the checkbox in the "Search Tags" List
   * if the state is being changed.
   */
  isTagToSelectCheckboxBeingToggled(tag: TagInstance): boolean {
    return this.selectTagsBeingToggled.has(tag?.id);
  }

  /**
   * Shows the SimpleSuccessErrorDialog letting the User know they've hit the maximum number of
   * Tags that can be selected at once.
   */
  private showSelectionLimitReachedDialog(): void {
    this.simpleSuccessErrorDialogComponent.showErrorDialog(
      "Selection Limit Reached",
      `You've reached the maximum of ${MonitorTags_MaxSelectedTags} selected Tags. Uncheck an existing Tag before selecting another.`
    );
  }

  /**
   * Records that the Tag with the given ID was selected while the given Search Option / Search
   * Value Filter was active, creating a new Filter Tag for that Filter if one doesn't already exist.
   * Does nothing if no Filter (a non-empty Search Value) was actually in use at the time.
   */
  private recordTagSelectionUnderFilter(tagId: number, searchOption: SearchOption | undefined, searchValue: string | undefined): void {
    if (tagId == null || searchValue == null || searchValue.length === 0) {
      return;
    }

    let filterTag = this.filterTags.find(t => this.isSameFilterTag(t, searchOption, searchValue));
    if (filterTag == null) {
      filterTag = new SelectionFilterTag(searchOption, searchValue);
      this.filterTags.push(filterTag);
    }
    if (!filterTag.tagIds.includes(tagId)) {
      filterTag.tagIds.push(tagId);
    }
  }

  /**
   * Removes the given Tag ID from every Filter Tag that references it (since it's no longer
   * selected), dropping any Filter Tag that ends up with no Tags left.  If the Filter Tag currently
   * being "viewed" gets dropped, clears that too so the "Select Tags" List goes back to showing
   * normal Search results.
   */
  private removeTagFromFilterTags(tagId: number): void {
    this.filterTags.forEach(filterTag => filterTag.tagIds = filterTag.tagIds.filter(id => id !== tagId));
    this.filterTags = this.filterTags.filter(filterTag => filterTag.tagIds.length > 0);
    if (this.activeFilterTag != null && !this.filterTags.includes(this.activeFilterTag)) {
      this.activeFilterTag = null;
    }
  }

  /**
   * Returns a boolean indicating if the given Filter Tag represents the same Filter as the given
   * Search Option / Search Value (same Search Type, same Property if searching by Property, and
   * the same Search Value).
   */
  private isSameFilterTag(filterTag: SelectionFilterTag, searchOption: SearchOption | undefined, searchValue: string | undefined): boolean {
    if (filterTag.searchValue !== searchValue || filterTag.searchOption?.searchType !== searchOption?.searchType) {
      return false;
    }
    if (filterTag.searchOption?.searchType === SearchType.PROPERTY) {
      return filterTag.searchOption?.property?.id === searchOption?.property?.id;
    }
    return true;
  }

  /**
   * Returns the display label for a Filter Tag - the same "Search Option" label shown in the Search
   * Bar's dropdown, plus the Search Value that was entered.
   */
  getFilterTagLabel(filterTag: SelectionFilterTag): string {
    const optionLabel = filterTag.searchOption?.searchType === SearchType.NAME
      ? "NAME"
      : filterTag.searchOption?.property?.name;
    return `${optionLabel}: ${filterTag.searchValue}`;
  }

  /**
   * Called by the UI when a Filter Tag itself (not its Cancel "x") is clicked.  Re-runs the
   * backend Search using that Filter's Search Option / Search Value so the "Select Tags" List
   * shows the full set of matching Tags (not just the ones already selected), until the User
   * performs a new Search or clicks the Filter Tag again to go back to browsing.
   */
  async onFilterTagClicked(filterTag: SelectionFilterTag): Promise<void> {
    const isTogglingOff = this.activeFilterTag === filterTag;

    this.selectedTagsToSelectSearchOption = isTogglingOff ? this.tagsToSelectSearchOptions[0] : (filterTag.searchOption ?? this.tagsToSelectSearchOptions[0]);
    this.tagsToSelectSearchValue = isTogglingOff ? "" : filterTag.searchValue;

    this.isSearchingTagsToSelect = true;
    await this.searchTagsToSelect();

    this.activeFilterTag = isTogglingOff ? null : filterTag;
  }

  /**
   * Called by the UI when a Filter Tag's Cancel ("x") is clicked.  Unselects every Tag that was
   * selected under that Filter - this also drops the Tag itself, since it'll have no Tags left
   * (see "removeTagFromFilterTags").
   */
  async onFilterTagCancelClicked(filterTag: SelectionFilterTag, event: Event): Promise<void> {
    event.stopPropagation();

    const tagsToUnselect = this.tagsSelected.filter(tag => filterTag.tagIds.includes(tag?.id));
    const promises: Promise<void>[] = [];
    tagsToUnselect.forEach(tag => promises.push(this.removeSelectTagFromTagList(tag)));
    if (promises.length > 0) {
      await Promise.all(promises);
    }
  }

  /**
   * Returns a boolean indicating if all the Tags in the "Selected Tags"
   * List have themselves been selected to be tracked.  This is used by the UI
   * to determine if the "ALL" checkbox should be checked.
   */
  isAllSelectedTagsSelected(): boolean {
    if (this.tagsSelected.length == 0) {
      return false;
    }
    return this.tagsSelected.find(tag => !this.tagsBeingTracked.get(tag.id).movementEventsEnabled) == null;
  }

  /**
   * Starts the "Select All" / "Unselect All" Tags in the "Selected Tags"
   * List.   
   * 
   * This sets a boolean flag and kicks off the async process.
   */
  selectAllSelectedTags(select: boolean): void {
    if (!this.isSelectingAllSelectedTags) {
      this.isSelectingAllSelectedTags = true;
      this.selectAllSelectedTagsAsync(select);
    }
  }

  /**
   * This is the "Select All" / "Unselect All" Process for the Tags in the 
   * "Selected Tags" List that runs in the background.
   * 
   * This either adds any that are missing to the map displays on the left,
   * or removes any that are drawn depending on the situation.  Then it 
   * resets the boolean flag indicating that the process is running (for UI 
   * purposes)
   */
  private async selectAllSelectedTagsAsync(select: boolean): Promise<void> {
    console.log(`Selecting All Selected Tags: ${select}`);

    // Kick off processes to either start / stop tracking any unselected / selected Tags depending on the appropriate situation
    const promises: Promise<void>[] = [];
    if (select) {
      this.selectedTagsToDisplay.forEach(tag => {
        if (!this.tagsBeingTracked.get(tag.id).movementEventsEnabled) {
          this.tagsSelectedBeingToggled.set(tag.id, tag);
          promises.push(this.startTrackingSelectedTag(tag));
        }
      });
    } else {
      this.selectedTagsToDisplay.forEach(tag => {
        if (this.tagsBeingTracked.get(tag.id).movementEventsEnabled) {
          this.tagsSelectedBeingToggled.set(tag.id, tag);
          promises.push(this.stopTrackingSelectedTag(tag));
        }
      });
    }

    // Wait for all the kicked off processes to finish...
    if (promises.length > 0) {
      await Promise.all(promises);
    }

    // Update the "isSelectingAllSelectedTags" back to false to indicate the process is no longer running
    this.isSelectingAllSelectedTags = false;
  }

  /**
   * Called by the UI when a "Selected Tag"'s checkbox is toggled to 
   * either start or stop being tracked / displayed in a Map on the RHS.  
   */
  onSelectedTagToggled(tag: TagInstance, event): void {
    console.log(`On Selected Tag Toggled!  Selected Tag: '${tag?.id}/${tag?.name}', Checked: ${event?.target?.checked}`);
    if (tag?.id == null) {
      console.warn("Ignoring Selected Tag being toggled since ID was not found.");
      return;
    }

    // Add the Asset being selected to the Map of Tags being toggled....
    this.tagsSelectedBeingToggled.set(tag.id, tag);

    // Depending on if this is a checked / unchecked event, handle the selection...
    if (event?.target?.checked) {
      this.startTrackingSelectedTag(tag);
    } else {
      this.stopTrackingSelectedTag(tag);
      this.refreshDisplayedSelectedTagsInList();
    }
  }

  /**
   * Called by the UI when the selected "Search Option" for the
   * search on the "Selected Tags" List is changed.
   * 
   * Re-searches the list if needed. 
   */
  onSelectedTagSearchOptionChanged(): void {
    // If the "Selected Tags" List is being searched, nothing needs to be done.
    if (this.isSearchingSelectedTags) {
      return;
    }
    // If the Search Value isn't specified, then we aren't displaying search results and nothing needs to be done.
    else if (this.selectedTagsSearchValue.length == 0) {
      return;
    }

    // Re-search the list
    this.isSelectedTagsSearchOptionChanging = true; 
    this.refreshDisplayedSelectedTagsInList();
    this.isSelectedTagsSearchOptionChanging = false;
  }

  /**
   * Starts tracking the given Asset in a Map on the RHS
   */
  private async startTrackingSelectedTag(tag: TagInstance): Promise<void> {
    // Retrieve a more "current" version of the Asset being tracked
    let freshTag: TagInstance = null;
    try {
      freshTag = await this.tagService.getTagInstanceById(tag.id);
    } catch (error) {
      console.warn("Problem obtaining fresh data on Tag being tracked.  Possibly stale data will be used.", error);
    }
    if (freshTag != null) {
      tag = freshTag;
      this.updateDisplayedTag(tag);
    }
    
    // Add the Tag's Location to the Display (if needed)
    if (tag.locationInstance != null) {
      await this.addLocationToDisplay(tag.locationInstance);
    }

    // Draw the Tag (if needed)
    if (this.locationBeingDisplayed != null && this.locationBeingDisplayed?.id == tag?.locationInstance?.id) {
      await this.mapComponent.addTagToDisplay(tag);
    }

    // Enable the Movement Events for the Tag
    this.tagsBeingTracked.get(tag?.id)?.enableMovementEvents();

    // Remove the Tag from the Map of Selected Tags which are being toggled.
    this.tagsSelectedBeingToggled.delete(tag.id);
  }

  /**
   * Stops tracking the given Asset in a Map on the RHS
   */
  private async stopTrackingSelectedTag(tag: TagInstance): Promise<void> {
    // Disable the Movement Events for the Tag since we are no longer tracking it
    this.tagsBeingTracked.get(tag?.id)?.disableMovementEvents();

    // If the currently drawn Location is the Location the Tag was in...
    if (tag?.locationInstance?.id == this.locationBeingDisplayed?.id) {
      // Remove the Tag from the Map
      await this.mapComponent.removeTagFromDisplay(tag);
    }

    // If the Map contains no other Tags, remove it so that another map may be drawn or a blank page if no other one exists.
    if (this.getSelectedTagsInLocation(tag.locationInstance).length == 0) {
      await this.removeLocationFromDisplay(tag.locationInstance);
    }

    // Remove the Tag from the Map of Selected Tags which are being toggled.
    this.tagsSelectedBeingToggled.delete(tag.id);
  }

  /**
   * Called by the UI to return a boolean indicating if the 
   * given Tag is being toggled between either being tracked 
   * in a map on the RHS or not.  
   * 
   * Used to disable the checkboxin the "Selected Tags" List 
   * while this is being done.
   */
  isSelectedTagBeingToggled(tag: TagInstance): boolean {
    return this.tagsSelectedBeingToggled.has(tag?.id);
  }

  /**
   * Adds the given Location to the Display available to be a Map
   * on the RHS.  If no Map is currently displayed, it becomes the 
   * active map. 
   */
  private async addLocationToDisplay(location: LocationInstance): Promise<void> {
    if (location?.id == null) {
      console.warn("NOT Adding Location to Display since ID could not be determined.");
      return;
    }
    if (this.locationsToDisplay.find(dl => dl?.id == location.id) != null) {
      console.warn("NOT Adding Location to Display since it is already added.");
      return;
    }

    // Add the Location to the Location's being displayed...
    this.locationsToDisplay.push(location);

    // Get a fresh instance of the Location from the backend (Call REST API)
    let freshLocation: LocationInstance = null;
    try {
      freshLocation = await this.locationService.getLocationInstanceById(location.id);
    } catch (error) {
      console.error(`Problem obtaining fresh information about Location to be displayed: '${location?.id}/${location?.name}'`, error);
    }
    // If a instance was obtained, update the local reference.  If not just continue with the stale data.
    if (freshLocation != null) {
      location.zones = freshLocation.zones;
    }

    // If no Location is currently being displayed, display the new Location
    if (this.locationBeingDisplayed == null) {
      this.locationBeingDisplayed = location;
      await this.drawLocation(location);
    }
  }

  /**
   * Removes the given Location as an option from the Locations that can be displayed.
   */
  private async removeLocationFromDisplay(location: LocationInstance): Promise<void> {
    console.log(`Removing Location: '${location?.id}/${location?.name}' from display...`);
    const displayedLocation = this.locationsToDisplay.find(displayedLocation => displayedLocation?.id == location?.id);
    if (displayedLocation == null) {
      console.warn("NOT removing Location from display as it isnt' currently being displayed!");
      return;
    }

    // Remove the Location from the array of displayed locations
    this.locationsToDisplay = this.locationsToDisplay.filter(displayedLocation => displayedLocation?.id != location?.id);

    // If the Location being removed was the displayed Location, display the next one in line
    if (this.locationBeingDisplayed?.id == displayedLocation.id) {
      const newLocationToDisplay = this.locationsToDisplay.length > 0? this.locationsToDisplay[0] : null;
      this.setLocationBeingDisplayed(newLocationToDisplay);
    }
  }

  /**
   * Called when a Location option being displayed as a Button at the top
   * of the Map is clicked.  This should switch to displaying the clicked 
   * on Location.
   */
  onLocationClicked(location: LocationInstance): void {
    console.log(`Location Clicked!  Location: '${location?.id}/${location?.name}'`);
    this.setLocationBeingDisplayed(location);
  }

  /**
   * This updates the current active Map to the given Location.
   */
  private async setLocationBeingDisplayed(location: LocationInstance): Promise<void> {
    console.log(`Setting displayed Location to: '${location?.id}/${location?.name}'...`);
    if ((location == null && this.locationBeingDisplayed == null) || (location?.id == this.locationBeingDisplayed?.id)) {
      console.warn("NOT changing displayed Location as it is not changing ")
      return;
    }

    // Erase the current Location being displayed
    await this.eraseMap();

    // Update the reference to the Location being displayed
    this.locationBeingDisplayed = location;

    // Draw the Location being displayed
    if (this.locationBeingDisplayed != null) {
      await this.drawLocation(this.locationBeingDisplayed);
    }
  }

  /**
   * Draws the given Location on the Floorplan and adds it's Zones.
   * This also adds the displays for any of the tracked Assets that 
   * are in the Map if they are selected.
   */
  private async drawLocation(location: LocationInstance): Promise<void> {
    console.log(`Drawing Location: '${location?.id}/${location?.name}'`);

    // Draw the Location's Floorplan on the Map... 
    await this.mapComponent.setDisplayedLocation(location);

    // Draw Promises that we need to wait for...
    const drawPromises: Promise<void>[] = [];

    // Draw the Zones in the Location
    location?.zones?.forEach(zone => drawPromises.push(this.mapComponent.addZoneToDisplay(zone)));

    // Draw the Tags in the Location
    this.getSelectedTagsInLocation(location)?.forEach(tag => drawPromises.push(this.mapComponent.addTagToDisplay(tag)));

    // Wait for everything to be drawn...
    await Promise.all(drawPromises);
  }

  /**
   * Erases the current map, removing all Tags / Zones 
   * before removing the Floorplan
   */
  private async eraseMap(): Promise<void> {
    await this.mapComponent.removeAllTagsFromDisplay();
    await this.mapComponent.removeAllZonesFromDisplay();
    await this.mapComponent.setDisplayedLocation(null);
  }

  /**
   * Returns the Tags that are selected to be tracked / displayed 
   * in the given Location.
   */
  getSelectedTagsInLocation(location: LocationInstance): TagInstance[] {
    return this.tagsSelected.filter(tag => this.tagsBeingTracked.get(tag?.id)?.movementEventsEnabled && tag?.locationInstance?.id == location?.id);
  }

  /**
   * Returns the number of Tags that have been checked / selected to be drawn on a map
   * in the RHS. Used by the UI to display the total number selected at the top of the 
   * Tab.
   */
  getCountOfSelectedTagsToBeDrawn(): number {
    return this.tagsSelected.filter(tag => this.tagsBeingTracked.get(tag?.id)?.movementEventsEnabled).length;
  }

  /**
   * Updates the following information of the displayed version of the Tag
   * with the given Tag's Data:
   *   1) Tag's Data (tag.data)
   *   2) Tag's Tag Type (tag.asset)
   *   3) Tag's Position
   *      a) Location (tag.locationInstance)
   *      b) Zone (tag.zoneInstance)
   *      c) Position (tag.position)
   */
  updateDisplayedTag(tag: TagInstance): void {
    // Sort the Data by the Property Order value
    tag?.data?.sort((a, b) => a.property.order - b.property.order);

    // Update the information in the "Select Tags" List
    {
      const selectTag = this.tagsToSelect.find(t => t?.id == tag?.id);
      if (selectTag != null) {
        selectTag.data = tag.data;
        selectTag.asset = tag.asset;
        selectTag.locationInstance = tag.locationInstance;
        selectTag.zoneInstance = tag.zoneInstance;
        selectTag.position = tag.position;
      }
    }

    // Update the information in the "Selected Tags" List
    {
      const selectedTag= this.tagsSelected.find(t => t?.id == tag?.id);
      if (selectedTag != null) {
        selectedTag.data = tag.data;
        selectedTag.asset = tag.asset;
        selectedTag.locationInstance = tag.locationInstance;
        selectedTag.zoneInstance = tag.zoneInstance;
        selectedTag.position = tag.position;
      }
    }
  }

  /**
   * Called by the given Tag's Web Socket connection to handle the 
   * given Event.  This should update the Tag's data according to the 
   * Event Type.  This may also update the Map if the Tag is being 
   * drawn / tracked.
   */
   private async handleTagWebSocketMessage(message: MessageEvent<any>, tag: TagInstance): Promise<void> {
    // Parse the MessageEvent data as the expected EventInstance
    const event = JSON.parse(message.data) as EventInstance;

    // Make sure the UUID of the Event Type exists so it can be compared against
    if (event?.event?.uuid == null) {
      console.warn("Ignoring unknown / unhandled event from 'Tag Instance' (TAG) Web Socket.");
      return;
    }

    // Depending on the Event Type, handle the Event...
    switch (event.event.uuid) {
      case EventUuid_TagEnteredZone: {
        // Tag Entered a Zone, update the Zone of the Tag based on the Event
        tag.zoneInstance = event.zoneInstance;
        break;
      }
      case EventUuid_TagExitedZone: {
        // Tag Entered a Zone, update the Zone of the Tag to NULL since it is not known
        tag.zoneInstance = null;
        break;
      }
      case EventUuid_TagEnteredLocation: {
        // Tag Entered a Location, update the Location / Position of the Tag based on the Event
        tag.locationInstance = event.locationInstance;
        tag.position = event.point;

        // If the Tag is also being drawn, then need to update the displays
        if (this.tagsBeingTracked.get(tag?.id)?.movementEventsEnabled) {
          // Add the Tag's New Location to the Location's being displayed (only adds it if new)
          await this.addLocationToDisplay(tag.locationInstance);

          // if the Tag's New Location is the Location being displayed, draw the Tag on the Map
          if ((tag?.locationInstance?.id == this.locationBeingDisplayed?.id) && (tag.position != null)){
            await this.mapComponent.addTagToDisplay(tag);
          }
        }
        break;
      }
      case EventUuid_TagExitedLocation: {
        const oldLocation = tag.locationInstance;
        // Tag Exited a Location, update the Location / Position to NULL since it is not known.
        tag.locationInstance = null;
        tag.zoneInstance = null;
        tag.position = null;

        // If the Tag is also being drawn, then need to update the displays
        if (this.tagsBeingTracked.get(tag?.id)?.movementEventsEnabled) {
          if (oldLocation != null) {
            // If the Tag was previously in the displayed Location, remove it from the map
            if (oldLocation?.id == this.locationBeingDisplayed?.id) {
              await this.mapComponent.removeTagFromDisplay(tag);
            }
            // If the Tag's old Location no longer contains any Tags being tracked / displayed, then remove the Location from the displayed list
            if (this.getSelectedTagsInLocation(oldLocation).length == 0) {
              await this.removeLocationFromDisplay(oldLocation);
            }
          }
        }
        break;
      }
      case EventUuid_TagMovement: {
        // Tag Moved!  Update the Tag's Postion to the position from the event
        tag.position = event.point;

        // If the Tag is also being drawn, then need to update the displays
        if (tag?.locationInstance?.id == this.locationBeingDisplayed?.id) {
          if (tag.position != null) {
            if (this.mapComponent.isTagDisplayed(tag)) {
              await this.mapComponent.updateTagPosition(tag);
            } else {
              await this.mapComponent.addTagToDisplay(tag);
            }
          } else {
            if (this.mapComponent.isTagDisplayed(tag)) {
              await this.mapComponent.removeTagFromDisplay(tag);
            }
          }
        }
        break;
      }
      default: {
        console.warn(`Unknown Event seen for Tag.  Event: '${event?.event?.id}/${event?.event?.name}/${event?.event?.uuid}'!`);
        break;
      }
    }
  }

  /**
   * Returns a boolean indicating if the given "Search Tag" has already been 
   * selected to be in the "Selected Tags" List. 
   */
  public isTagToSelectSelected(tag: TagInstance): boolean {
    return this.tagsSelected.find(ts => ts?.id == tag?.id) != null;
  }

  /**
   * Called when the Row Header is clicked on an Tag in the Selected Tags List.
   * This either shows / hides the List Body depending on it's current state.
   */
  onSelectedListHeaderClicked(tag: TagInstance): void {
    console.log(`On Selected List Header Clicked: '${tag?.id}/${tag?.name}'!`)
    if ($(`#${this.getIdForSelectedListDisplayBody(tag)}`).hasClass('hidden')) {
      this.showSelectedListBody(tag);
    } else {
      this.hideSelectedListBody(tag);
    }
  }

  private async showSelectedListBody(tag: TagInstance): Promise<void> {
    // Fetch an updated data of the Tag from the Server...
    let freshTag: TagInstance = null;
    try {
      freshTag = await this.tagService.getTagInstanceById(tag.id);
    } catch (error) {
      console.error("Problem obtaining fresh Data for Asset when showing the List Body!  Stale Data will be used.", error);
    }
    if (freshTag != null) {
      this.updateDisplayedTag(freshTag);
    }

    // Display the List Body
    $(`#${this.getIdForSelectedListDisplayBody(tag)}`).removeClass('hidden');
  }

  private async hideSelectedListBody(tag: TagInstance): Promise<void> {
    $(`#${this.getIdForSelectedListDisplayBody(tag)}`).addClass('hidden');
  }

  /**
   * Returns a boolean indicating if the given "Selected Tag" has already been
   * selected to be displayed in the map on the RHS. 
   */
  public isSelectedTagSelectedToBeDisplayed(tag: TagInstance): boolean {
    return this.tagsBeingTracked.get(tag.id).movementEventsEnabled;
  }

  /**
   * Returns the ID of the HTML Element used for the Display Body 
   * for an Tag in the Selected Assets List.
   */
  getIdForSelectedListDisplayBody(tag: TagInstance): string {
    return `mtstlb_${tag?.id}`;
  }

  /**
   * Returns the ID of the HTML Container being used to display
   * the Full Details for the given Tag.
   * Used for showing / hiding the Full Details Display.
   */
  getIdForSelectedListFullDetails(tag: TagInstance): string {
    return `mtstlfd_${tag.id}`;
  }

  /**
   * Returns the ID of the HTML Element being used to display the
   * option for displaying the Full Details when clicked. 
   * Used for showing the Full Details on click, and toggling the display
   * of this HTML Element when appropriate. 
   */
  getIdForSelectedListShowFullDetails(tag: TagInstance): string {
    return `mtstlsfd_${tag.id}`;
  }

  /**
   * Returns the ID of the HTML Element being used to display the
   * option for hiding the Full Details when clicked. 
   * Used for hiding the Full Details on click, and toggling the display
   * of this HTML Element when appropriate. 
   */
  getIdForSelectedListHideFullDetails(tag: TagInstance): string {
    return `mtstlhfd_${tag.id}`;
  }

  /**
   * Shows the Full Details display for the given Tag.
   */
  showSelectedListFullDetails(tag: TagInstance): void {
    $(`#${this.getIdForSelectedListShowFullDetails(tag)}`).hide();
    $(`#${this.getIdForSelectedListHideFullDetails(tag)}`).show();
    $(`#${this.getIdForSelectedListFullDetails(tag)}`).show();
  }

  /**
   * Hides the Full Details display for the given Tag.
   */
  hideSelectedListFullDetails(tag: TagInstance): void {
    $(`#${this.getIdForSelectedListHideFullDetails(tag)}`).hide();
    $(`#${this.getIdForSelectedListShowFullDetails(tag)}`).show();
    $(`#${this.getIdForSelectedListFullDetails(tag)}`).hide();
  }

  /**
   * Returns the String URL for the <img>'s "src" property used
   * to display the image for the given Tag
   */
  public getImageSource(tag: TagInstance): string {
    return this.imageService.getImageSourceForTag(tag);
  }

  /**
   * Returns a boolean indicating if the Tag Image is present.
   * Used in the HTML to display the Image or not. 
   */
  public isImagePresent(tag: TagInstance): boolean {
    if (tag.image != null) {
      return true;
    }
    return false;
  }

  /**
   * Returns the String URL for the <img>'s "src" property used
   * to display the icon for the given Tag
   */
  public getIconSource(tag: TagInstance): string {
    return this.imageService.getIconSourceForTag(tag);
  }

  /*
   * Returns a boolean indicating if the "Full Details" section is needed for the given Tag.
   * It is only needed if the Tag has Property Data for a Property that isn't flagged as "Main Display"
   */
  isFullDetailsNeeded(tag: TagInstance): boolean {
    return tag?.data?.find(d => d?.property?.mainDisplay == false) != null;
  }

  /**
   * Returns a boolean indicating if the given Tag has any Functions that are exposed via
   * it's Tag Type that can be called / executed.  Used to display the Functions as Actions
   * in the Tags Tab when the Tag is expanded.
   */
  isContainsFunctions(tag: TagInstance): boolean {
    return tag?.asset?.functions?.length > 0;
  }

  /**
   * Called when a Function is clicked on in the Actions dropdown on an Tag.  The corresponding
   * Tag and the Function Key for the Function that was clicked are passed as parameters. 
   */
  openFunctionDialog(tag: TagInstance, functionKey: string): void {
    // TODO: Figure this out if needed / time permits
    //this.instanceFunctionDialogComponent.setInstanceAndFunction(tag, functionKey);
    //this.instanceFunctionDialogComponent.show();
  }

  /**
   * Handler for the TagFunctionDialogComponent which gets called on a successful Function
   * execution with an updated version of the Tag the function was performed on.
   * 
   * This replaces the Tag in the UI List with the up-to-date version so that changes
   * to the Property Data can be reflected in the UI.
   */
  onFunctionCompletedSuccessfully(tag: TagInstance): void {
    // Update the instance in the displayed list so that the data changes can be reflected there
    this.updateDisplayedTag(tag);

    // Hide the Instance Function Dialog Component
    this.instanceFunctionDialogComponent.hide();
  }

  /**
   * Called when the "Edit Data" Action is clicked in the Action(s) dropdown on an Tag. 
   * Displays the TagDataDialog to allow the User to edit the Data for the given Tag. 
   */
  openDataDialog(tag: TagInstance): void {
    // TODO: Figure this out if needed / time permits
    //this.instanceDataDialogComponent.setInstance(tag);
    //this.instanceDataDialogComponent.show();
  }

  /**
   * Handler for the TagDataDialogComponent which gets called on a successful "Update Data"
   * execution with an updated version of the Tag whose Data was updated.
   * 
   * This updates the version in the UI List with the information from the returned after update version
   * so that the updated Property Data can be reflected in the UI.
   */
  onDataUpdatedSuccessfully(tag: TagInstance): void {
    // Update the instance in the displayed list so that the data changes can be reflected there
    this.updateDisplayedTag(tag);

    // Hide the Instance Data Dialog Component
    this.instanceDataDialogComponent.hide();
  }

  /**
   * Returns a unique ID that is used for the Popup Dialog for the 
   * given Tag.
   */
   getIdForPopup(tag: TagInstance): string {
    return `mtTagPopup_${tag?.id}`;
  }

  /**
   * Gets the Hide Emitter for the given Tag.
   * These are used to "hide" the Popup Dialog for the Instances.
   */
  getHideEmitter(tag: TagInstance): EventEmitter<boolean> {
    return this.tagsBeingTracked.get(tag?.id)?.popupHideEventEmitter;
  }

  /**
   * Called when an Tag's Icon is clicked on the Map.
   */
  public onTagClickedOnMap(event: TagClickedEvent): void {
    console.log(`onTagClicked()!  Tag: '${event?.tag?.id}/${event?.tag?.name}'`);

    // Hide the Popup Dialog if it is currently being displayed...
    this.getHideEmitter(event?.tag)?.emit(true);
 
    // Show the Popup Dialog at the mouse click
    $(`#${this.getIdForPopup(event?.tag)}`)?.css('left', `${event.event.stageX + $('#locatetab').outerWidth(true)}px`);
    $(`#${this.getIdForPopup(event?.tag)}`)?.css('top', `${event.event.stageY + $('#locationsWell').outerHeight(true)}px`);
    $(`#${this.getIdForPopup(event?.tag)}`)?.show();

    /*
     * The below piece of code is needed since the top level "role='dialog'" <div> element used for the Dialog
     * is being padded with some extra invisible space underneath the Dialog.  This is preventing click events from
     * being detected on Icons that are visible underneath the Dialog since it was being blocked by the invisible part.
     * This resizes the outer "role='dialog'" <div> element to that of the inner "role='document'" <div> element. 
     * Hacky, but works.
     */
    $(`#${this.getIdForPopup(event?.tag)}`)?.css('height', $(`#${this.getIdForPopup(event?.tag)}_Document`)?.height() + 1);
  }

  /**
   * Refreshes the "selectedTagsToDisplay" TagInstance[] based on the currently values
   * in the "tagsSelected" TagInstance[] and the current "Selected Tags" Search
   * Values. These are the Tags that should be shown in the "Selected Tags" List
   */
  private refreshDisplayedSelectedTagsInList(): void {
    const tags: TagInstance[] = [];
    const searchValue = this.selectedTagsSearchValue;
    const searchOption = this.selectedTagsSearchOption;

    if (searchValue.length == 0) {
      tags.push(...this.tagsSelected);
    } else {
      // Searching Assets, only display the ones that should be based on the search criteria
      tags.push(...this.tagsSelected.filter(st => this.shouldTagBeDisplayedInList(st, searchOption, searchValue)));
    }
    this.selectedTagsToDisplay = tags;
  }

  /**
   * Returns a boolean indicating if the given Tag should be displayed in the "Selected Tags"
   * List based on the given search criteria.
   */
  private shouldTagBeDisplayedInList(tag: TagInstance, searchOption: SearchOption, searchValue: string): boolean {
    // If the Tag is being tracked, then it should be in the List
    if (this.tagsBeingTracked?.get(tag?.id)?.movementEventsEnabled) {
      return true;
    }

    // Instance isn't being tracked, then it needs to pass the Search Criteria
    switch (searchOption?.searchType) {
      case SearchType.NAME: {
        /*
         * Search by Name
         * Returns true if the search value is anywhere in the Instance's Name (case in-sensitive). 
         */
        return tag.name.toLowerCase().indexOf(searchValue.toLowerCase()) > -1;
      }
      case SearchType.PROPERTY: {
        /*
         * Search by Property
         * Returns true if the Tag Data for the Property being searched by 
         * and if the data matches criteria depending on the search value:
         *  1) Search Value = "?" 
         *     As long as Data exists and is specified for the Property, the 
         *     Tag should be shown so true will be returned.
         *  2) Anything Else
         *     Depends on the Property Type: 
         *       A) INTEGER
         *          Returns true if the search value is the same numerically as the 
         *          matching data value
         *       B) STRING / Anything Else
         *          Returns true if the search value is anywhere in the matching
         *          data value (case in-sensitive). 
         */
        // Find any matching Instance Data for the Property specified in the search
        const dataForProperty = tag?.data?.filter(datum => datum?.property?.id == searchOption?.property?.id);
        
        // If no data was found for the Property, then the Tag should not be shown.
        if (dataForProperty.length == 0) {
          return false;
        }

        /*
         * 1) If Search Value is "?", then as long as data is specified that 
         * contains a value, return true.
         */
        if (searchValue == '?') {
          return dataForProperty.find(datum => datum?.value?.trim()?.length > 0) != null;
        }

        /*
         * 2) Anything Else, search based on the Property Type of the Property specified
         * in the search criteria. 
         */
        switch (searchOption?.property?.type) {
          case PropertyType.INTEGER: {
            /* 
             * A) INTEGER - Return true if the search value is the same numerically as the 
             *    matching data value
             */
            return dataForProperty.find(datum => Number(datum?.value) == Number(searchValue)) != null;
          }
          case PropertyType.STRING: 
          default: {
            /*
             * B) STRING / Anything Else - Return true if the search value is anywhere in 
             *    the matching data value (case in-sensitive). 
             */
            return dataForProperty.find(datum => datum?.value?.toLowerCase()?.indexOf(searchValue?.toLowerCase()) > -1) != null;
          }
        }
      }
      default: {
        console.warn(`Unknown Search Type: '${searchOption?.searchType}'!  Tags will pass search and be in the list.`);
        return true;
      }
    }
  }

  /**
   * Called by the UI when the "X" Delete Option is selected for the given 
   * Stored Tag List.
   * 
   * This stores a reference to the List selected, then displays the 
   * SimpleConfirmDialog to make sure the User wanted to deleted the List.
   * The "onConfirmDialogCancelClicked" and "onConfirmDialogConfirmClicked"
   * methods would then be used to continue this process.
   */
  onDeleteClickedForStoredTagList(tagList: StoredTagListItem): void {
    this.tagListBeingDeleted = tagList;
    this.simpleConfirmDialogComponent.setMessage(`Are you sure you want to delete list: '${tagList?.name}'?`);
    this.simpleConfirmDialogComponent.showDialog();
  }

  /**
   * Background process for deleting a Stored Tag List.  This removes it from 
   * the "storedTagList" array variable then saves that variable in the cookie.
   */
  private async deleteStoredTagList(tagList: StoredTagListItem): Promise<void> {
    this.storedTagList = this.storedTagList.filter(storedList => storedList?.name != tagList?.name);
    this.saveStoredTagList();
    this.isDeletingStoredTagList = false;
  }

  /**
   * Saveds the "storedAssetList" array variable in the cookie.
   */
  private saveStoredTagList(): void {
    const value = JSON.stringify(this.storedTagList);
    console.log("Saving Stored Tag Lists Vale: " + value);
    this.cookieService.set('page[monitor-tags]-key[saved-tags-list]', value, 1, '/', null, false, 'Lax');
  }

  /**
   * Returns a boolean indicating if any Tags are currently in the "Selected Tags"
   * List.  This is used by the UI to disable some things.
   */
  isAnyTagSelected(): boolean {
    return this.tagsSelected.length > 0;
  }

  /**
   * Returns a boolean indicating if there are any Stored Tag Lists currently
   * saved in the cookie.  This is used by the UI to disable some things.
   */
  isAnySavedStoredListItemPresent(): boolean {
    return this.storedTagList.length > 0;
  }

  /**
   * Called by the UI when the "SAVE CURRENT LIST" Button is clicked.
   * Kicks off the "Save Tag List" background process if needed.  This 
   * starts by showing the SaveTagListDialogComponent.
   */
  onSaveCurrentListClicked(): void {
    if (this.isAnyTagSelected() && !this.isSavingCurrentTagList && !this.isLoadingTagList) {
      this.isSavingCurrentTagList = true;
      this.saveTagListDialogComponent.setUiEnabled(true);
      this.saveTagListDialogComponent.showDialog(this.lastLoadedOrSavedTagList?.name);
    }
  }

  /**
   * Called when the SaveTagListDialogComponent's "Save" Button is clicked.
   * Performs the "Save" Logic for the Tag List. 
   */
  async onSaveTagListDialogSaveClicked(name: string): Promise<void> {
    this.saveTagListDialogComponent.setUiEnabled(false);

    // Create a New StoredTagListItem for the List being saved
    const newListItem = { name: name, tagIds: []} as StoredTagListItem;
    this.tagsSelected.forEach(tag => newListItem.tagIds.push(tag?.id));

    /*
     * Build a new StoredTagListItem[] to be used as the Tag List.
     * Add the List being saved at the top, then follow by any previously
     * stored Tag List that doesn't match the name. (Same name rewrites)
     */
    const newList: StoredTagListItem[] = [];
    newList.push(newListItem);
    newList.push(...this.storedTagList.filter(tagList => tagList?.name != name));
    this.storedTagList = newList;

    // Set the reference to the last loaded / saved Tag List to the saved one. (Used to re-populate the "Save Tag List" Dialog's Name)
    this.lastLoadedOrSavedTagList = newListItem;

    // Save the newly created Tag List, Hide the Dialog and update the flag
    this.saveStoredTagList();
    this.saveTagListDialogComponent.hideDialog();
    this.isSavingCurrentTagList = false;
  } 

  /**
   * Called when the SaveTagListDialogComponent's "Cancel" Button is clicked.
   * Closes the Dialog and cancels the save.
   */
  async onSaveTagListDialogCancelClicked(): Promise<void> {
    this.saveTagListDialogComponent.hideDialog();
    this.isSavingCurrentTagList = false;
  }

  /**
   * Called by the UI when a Saved Tag List is clicked.
   * This kicks off the "Load Tag List" background process if needed.
   */
  onTagListClicked(tagList: StoredTagListItem): void {
    console.log(`On Tag List Clicked: '${tagList?.name}'`);
    if (!this.isLoadingTagList && !this.isLoadingTagList) {
      this.isLoadingTagList = true;
      this.loadTagList(tagList);
    }
  }

  /**
   * "Load Tag List" Background Process.
   * This loads the Tag's for all the Tag Ids in the given Saved Tag List
   * and then adds any that are retrieved to the "Selected Tags" List for tracking.
   */
  private async loadTagList(tagList: StoredTagListItem): Promise<void> {
    // Remove all the currently selected Tags as they are to be replaced with the saved list
    this.removeAllSelectedTags();

    // Load all the Tags from the List 
    const promises: Promise<void>[] = [];
    tagList?.tagIds?.forEach(tagId => promises.push(this.loadAndDisplayTag(tagId)));
    if (promises.length > 0) {
      await Promise.all(promises);
    }

    // Set the reference to the last loaded / saved Asset List to the loaded one. (Used to re-populate the "Save Asset List" Dialog's Name)
    this.lastLoadedOrSavedTagList = tagList;

    // Reset the Loading Flag back to false since it's done
    this.isLoadingTagList = false;
  }

  /**
   * Loads the Tag by calling the REST API to obtain it's information using 
   * the given ID, and if one was returned adds it to the "Selected Tags" List
   * before returning.
   */
  private async loadAndDisplayTag(tagId: number): Promise<void> {
    console.log(`Loading and Displaying Tag: ${tagId}`);
    // Call the REST API to load the Tag for the Tag ID
    let tag: TagInstance = null;
    try {
      tag = await this.tagService.getTagInstanceById(tagId);
    } catch (error) {
      console.error(`Problem obtaining Tag for Tag ID: ${tagId}!`, error);
    }

    // If an Tag was obtained, add it to the "Selected Tags" List
    if (tag != null) {
      await this.addSelectTagToTagList(tag, false);
    }
  }

  /**
   * Removes all Tags in the "Selected Tags" List from that List.
   */
  private async removeAllSelectedTags(): Promise<void> {
    const tagsToRemove: TagInstance[] = [];
    tagsToRemove.push(...this.tagsSelected);

    const promises: Promise<void>[] = [];
    tagsToRemove.forEach(tag => promises.push(this.removeSelectTagFromTagList(tag)));
    if (promises.length > 0) {
      await Promise.all(promises);
    }
  }

  /**
   * Called when a User clicks the "Confirm" option on the SimpleConfirmDialog.
   * This deletes the Tag List from the array being stored and closes
   * the SimpleConfirmDialog.
   */
  async onConfirmDialogConfirmClicked(): Promise<void> {
    if (this.tagListBeingDeleted != null) {
      this.isDeletingStoredTagList = true;
      await this.deleteStoredTagList(this.tagListBeingDeleted);
    }
    this.tagListBeingDeleted = null;
    this.simpleConfirmDialogComponent.hideDialog();
  }

  /**
   * Called when a User clicks the "Cancel" option on the SimpleConfirmDialog.
   * This just closes the SimpleConfirmDialog.
   */
  onConfirmDialogCancelClicked(): void {
    this.tagListBeingDeleted = null;
    this.simpleConfirmDialogComponent.hideDialog();
  }

  async onPageChange(pageNumber?: number): Promise<void> {
      // Validate page number
      if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
        return;
      }

      // Avoid redundant reloads
      if (this.currentPage === pageNumber) {
        return;
      }

      this.currentPage = pageNumber;

      // Fetch the requested page from the server
      this.isSearchingTagsToSelect = true;
      await this.loadTagsToSelectPage(pageNumber);
      this.isSearchingTagsToSelect = false;
  }

}

/**
 * Class designed to hold the Web Socket subscription information
 * for the Tags being tracked.  This also holds the
 * "Hide" Event Emitter used for the Popup Dialogs since it will
 * only be needed if the Instances are actively tracked.
 */
class WsConnection {
  /** Web Socket Components used for the connection */
  wsSubject: Subject<any>;
  wsSubscription: Subscription;

  /** Popup Hide Emitter used in UI Handling.  Stored here for convienence since it is only used when tracked. */
  popupHideEventEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  /** Flag indicating if the Movement Events have been enabled over the web socket or not */
  movementEventsEnabled = false;

  /**
   * Enables the Movement Events across the Web Socket (if needed)
   */
  enableMovementEvents(): void {
    const message: MonitorTagsWsControlMessage = { send_movement_events: true };
    this.wsSubject.next(message);
    this.movementEventsEnabled = true;
  }

  /**
   * Disables the Movement Events across the Web Socket (if needed)
   */
  disableMovementEvents(): void {
    const message: MonitorTagsWsControlMessage = { send_movement_events: false };
    this.wsSubject.next(message);
    this.movementEventsEnabled = false;
  }

  /**
   * Closes the WS Connection
   */
  close() {
    if (this.wsSubject != null) {
      this.wsSubject.unsubscribe();
      this.wsSubject = null;
    }
    if (this.wsSubscription != null) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = null;
    }
    this.movementEventsEnabled = false;
  }
}

/**
 * Different Types of Searches
 */
enum SearchType {
  /** Search by Instance Name */
  NAME = "NAME",
  /** Search by Property Data Value */
  PROPERTY = "PROPERTY"
}

/**
 * Search Option for Tags
 */
class SearchOption {
  /** Search Type - NAME / PROPERTY */
  searchType: SearchType;
  /** Property being search by if the Search Type is PROPERTY */
  property: Property;

  constructor(property?: Property) {
    this.property = property;
    this.searchType = this.property == null? SearchType.NAME : SearchType.PROPERTY;
  }
}

/**
 * Structure of the control message sent across the Monitor Tags Web Socket connection
 * used to enable / disable the Movement Events.
 */
interface MonitorTagsWsControlMessage {
  send_movement_events: boolean;
}

interface StoredTagListItem {
  name: string;
  tagIds: number[];
}

/**
 * Represents a Filter (Search Option + Search Value) that at least one currently selected Tag was
 * selected under.  Displayed as a "tag" below the "Select Tags" Search Bar so the User can jump back
 * to reviewing - or bulk-unselect - what they picked while that Filter was active.
 */
class SelectionFilterTag {
  /** Search Option (NAME / a specific Property) the Filter was searching by. */
  searchOption: SearchOption | undefined;
  /** Search Value that was entered for the Filter. */
  searchValue: string;
  /** IDs of the currently selected Tags that were selected while this Filter was active. */
  tagIds: number[] = [];

  constructor(searchOption: SearchOption | undefined, searchValue: string) {
    this.searchOption = searchOption;
    this.searchValue = searchValue;
  }
}
